import React, { Fragment, useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  GridLegacy as Grid,
  DialogContentText,
} from "@mui/material";
import {
  api,
  AssetLocation,
  CredentialType,
  IUserCreate,
  IUserGenerated,
  IUserUpdate,
  RequestStatus,
  User,
  UserType,
  IListRolesResponse,
} from "../../logic/api";
import NotchedCard from "../cards/NotchedCard/NotchedCard";
import EditableGroup, {
  EditableGroupType,
} from "../editable/EditableGroup/EditableGroup";
import { SessionContext } from "../../utility/useSession";
import EditableField, {
  EditableFieldTypes,
} from "../editable/EditableField/EditableField";
import { ProgressView } from "../notifications/progress/ProgressView";
import ErrorDialog from "../notifications/errorDialog/ErrorDialog";
import styles from "./styles";
import TwoFactorRegistration from "../twoFactor/twoFactorRegistration";
import UserCarParkAccess from "./UserCarParkAccess";
import CarParkField from "../editable/EditableField/fields/CarParkField";

import { useLabelPrinter } from "../labels";
import { useLabelTemplates } from "../labels/labelTemplates";
import { IDropDownListItem } from "../editable/EditableField/fields/DropDownField";
import { formatCamel, mapRoleNameToDescription, isAdmin, isSysAdmin } from "../../utility/utils";
import { useTranslation } from "react-i18next";
import { ReleaseFlag, useReleaseManagement } from "../../utility/feature-management";
import ServiceUserTokenManagement from "./ServiceUserTokenManagement";
import ServiceUserTokenManagementV2 from "./ServiceUserTokenManagementV2";

const UserDetailDialog: React.FunctionComponent<{
  user: User | null;
  show: boolean;
  availableRoles: IListRolesResponse['roles'];
  onClose: (didSave: boolean, user: User | null) => void;
}> = (props) => {
  const { labelPrinter } = useLabelPrinter();
  const { generateUserPrintJob } = useLabelTemplates();
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const isKeyMasterRoleReleased = useReleaseManagement(ReleaseFlag.releaseKeyMasterRole);
  const isRbacReleased = useReleaseManagement(ReleaseFlag.releaseRbac);
  const isDeprecateShowUserInLocationViewReleased = useReleaseManagement(ReleaseFlag.releaseDeprecateShowUserInLocationView);
  const isAgencyFlagReleased = useReleaseManagement(ReleaseFlag.releaseAgencyFlagToUser);
  const isCreateServiceUserReleased = useReleaseManagement(ReleaseFlag.releaseCreateServiceUser);
  const isTokenManagementV2Released = useReleaseManagement(ReleaseFlag.releaseServiceUserTokenManagementV2);
  const isUserExternalRefReleased = useReleaseManagement(ReleaseFlag.releaseUserExternalRef);

  const createUser = api.hooks.users.create();
  const updateUser = api.hooks.users.update();
  const resetUserPassword = api.hooks.users.resetPassword();
  const resetUserStaffPass = api.hooks.users.resetStaffPass();
  const disable2FA = api.hooks.twoFactor.deactivate();
  const createIntegrationToken = api.hooks.integrationToken.createToken();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const [generatedUser, setGeneratedUser] = useState<IUserGenerated | null>();
  const [showGeneratedUser, setShowGeneratedUser] = useState(false);

  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [showTwoFactorRegistration, setShowTwoFactorRegistration] = useState(
    false
  );
  const [showTokenManagement, setShowTokenManagement] = useState(false);
  const [showResetStaffPass, setShowResetStaffPass] = useState(false);
  const [showResetStaffPassConfirmed, setShowResetStaffPassConfirmed] = useState(false);
  const [showDisableTwoFactor, setShowDisableTwoFactor] = useState(false);
  const [showDisableTwoFactorConfirmed, setShowDisableTwoFactorConfirmed] = useState(false);

  const [saved, setSaved] = useState(false);

  const defaultCarPark: AssetLocation =
    session.current.site == null
      ? ({ id: 1 } as AssetLocation)
      : session.current.site.user.defaultCarPark;

  const [user, setUser] = useState<IUserCreate | IUserUpdate>({
    active: true,
    defaultCarPark: defaultCarPark,
    carParkAccess: defaultCarPark == null ? "" : `|${defaultCarPark.id}|`,
  } as IUserCreate);

  let isServiceAccount = false;

  if (user != null) {
    isServiceAccount = [UserType.barrier, UserType.kiosk, UserType.mobile, UserType.service].includes(user.userType);
  }

  useEffect(() => {
    if (props.user != null) {
      setSaved(false);
      setUser({ ...props.user });
    }
  }, [props.user]);

  if (user == null) {
    return <Fragment></Fragment>;
  }

  if (session.current.site == null) {
    return <Fragment />;
  }

  const isNewUser = props.user == null;
  const isCurrentUser = session.current.site.user.id == (user as User).id;

  const isLoading =
    updateUser.status == RequestStatus.loading ||
    createUser.status == RequestStatus.loading ||
    resetUserPassword.status == RequestStatus.loading ||
    resetUserStaffPass.status == RequestStatus.loading;

  const resetUsersPassword = async () => {
    setShowPasswordReset(false);
    if (props.user != null) {
      resetUserPassword.send(
        session.current.context,
        {
          id: props.user.id,
        },
        (resetResult) => {
          if (resetResult == null) {
            return;
          }
          setGeneratedUser(resetResult);
          setShowGeneratedUser(true);
        }
      );
    }
  };

  const disableTwoFactor = async () => {
    setShowDisableTwoFactor(false);
    if (props.user != null) {

      disable2FA.send(
        session.current.context,
        {
          uuid: props.user.uuid,
        },
        (result) => {
          if (result == null) {
            return;
          }
          if (props.user != null) {
            props.user.twoFactorEnabled = false;
            if (isCurrentUser) {
              session.setUser({ ...props.user });
            }

          }



          setUser({ ...user, });
          setShowDisableTwoFactorConfirmed(true);
        }
      );

    }
  };


  const resetStaffPass = async () => {
    setShowResetStaffPass(false);
    if (props.user != null) {

      resetUserStaffPass.send(
        session.current.context,
        {
          id: props.user.id,
        },
        (resetResult) => {
          if (resetResult == null) {
            return;
          }
          if (props.user != null) {
            props.user.location.code = resetResult.staffPass;
          }

          setUser({ ...user, staffPass: resetResult.staffPass });
          setShowResetStaffPassConfirmed(true);
        }
      );

    }
  };

  const validateRoleSelection = (userData: IUserCreate | IUserUpdate): string | null => {
    if (isServiceAccount) return null; // Service accounts don't need role validation ?? confirm this

    if (isRbacReleased) {
      const userWithRoles = userData as User;
      const hasAnyRole = userWithRoles.roles && userWithRoles.roles.length > 0;

      return hasAnyRole ? null : t("users.validation.atLeastOneRoleRequired");
    } else {
      const hasAnyRole =
        userData.showUserInLocationView || // Driver
        userData.busDriver ||
        userData.transportManager ||
        userData.mngDriver ||
        userData.keyMaster ||
        userData.userType === UserType.admin;

      return hasAnyRole ? null : t("users.validation.atLeastOneRoleRequired");
    }
  };

  const isRoleAssigned = (roleName: string): boolean => {
    return (user as User).roles?.includes(roleName) || false;
  };

  const closeTokenManagement = () => {
    setShowTokenManagement(false);
  }

  const tokenManagementUser = props.user ?? generatedUser?.user;

  return (
    <Fragment>
      {saved == false && (
        <Dialog
          maxWidth={isLoading == true ? undefined : "sm"}
          fullWidth={isLoading == true ? false : true}
          open={props.show}
          keepMounted
          onClose={() => props.onClose(false, null)}
          aria-labelledby="alert-dialog-slide-title"
          aria-describedby="alert-dialog-slide-description"
        >
          {isLoading != true && (
            <DialogTitle id="alert-dialog-slide-title">
              <div style={{ textAlign: "center" }}>
                {isNewUser == true
                  ? t("users.newUser")
                  : isCurrentUser == true
                    ? t("users.myAccount")
                    : t("users.editUser")}
              </div>
            </DialogTitle>
          )}

          <DialogContent>
            <ProgressView
              showError={false}
              noDelay={true}
              status={isNewUser ? createUser.status : updateUser.status}
            >
              <div>
                <ErrorDialog err={disable2FA.err}></ErrorDialog>
                <ErrorDialog err={createUser.err}></ErrorDialog>
                <ErrorDialog err={updateUser.err}></ErrorDialog>
                <ErrorDialog err={resetUserPassword.err}></ErrorDialog>
                <ErrorDialog err={resetUserStaffPass.err}></ErrorDialog>
                <ErrorDialog err={createIntegrationToken.err}></ErrorDialog>
                {(props.user != null && props.user.twoFactorEnabled == true && (isCurrentUser == true || isSysAdmin(session))) && (
                  <Fragment>
                    <div style={{ textAlign: "center", padding: "10px" }}>
                      <b onClick={() => {
                        setShowDisableTwoFactor(true);
                      }} style={{ textDecoration: "underline", color: "red", cursor: "pointer" }}>
                        {t("users.disable2FA")}
                      </b>
                      <br />
                    </div>
                  </Fragment>
                )}

                {isCurrentUser == true && session.current.credential != null && (
                  <Fragment>
                    <div
                      style={
                        session.current.credential.twoFactorEnabled == true
                          ? styles.twoFactor
                          : styles.twoFactorNotProtected
                      }
                      onClick={() => {
                        setShowTwoFactorRegistration(true);
                      }}
                    >
                      {session.current.credential.twoFactorEnabled != true && (
                        <Fragment>
                          {t("users.setup2FA")}
                        </Fragment>
                      )}
                      {session.current.credential.twoFactorEnabled == true && (
                        <Fragment>
                          <div>
                            <b>
                              {t("users.accountProtected")}
                            </b>
                          </div>
                          <div>{t("users.changeDevice")}</div>
                        </Fragment>
                      )}
                    </div>
                    <TwoFactorRegistration
                      onClose={() => {
                        setShowTwoFactorRegistration(false);
                      }}
                      show={showTwoFactorRegistration}
                    />
                  </Fragment>
                )}



                <NotchedCard label={t("users.details")}>
                  <EditableGroup<IUserUpdate | IUserCreate>
                    id="user"
                    testId="user-details-group"
                    editType={EditableGroupType.alwaysEditing}
                    data={user}
                    dataFields={['userType', 'firstName', 'lastName', 'email', 'active', 'agency', 'externalRef']}
                    onDataChanged={async (data) => {
                      setUser({ ...user, ...data });
                      return true;
                    }}
                  >
                    <Grid container>
                      <EditableField<User, IDropDownListItem>
                        inputType={EditableFieldTypes.terminalField}
                        label={t("users.role")}
                        readOnly={isCurrentUser == true || (isNewUser == false && isServiceAccount == true)}
                        alwaysVisible={true}
                        onProvide={(data) => {
                          return {
                            name: "",
                            value:
                              data.userType == null
                                ? UserType.standard.toString()
                                : data.userType.toString(),
                          };
                        }}
                        onChange={(data, value) => {
                          data.userType = parseInt(value.value ?? "");
                        }}
                        onRequestData={async () => {
                          const items: IDropDownListItem[] = [];

                          let blockedTypes = [];

                          if (isCreateServiceUserReleased) {
                            if (isNewUser == true || (isNewUser == false && isServiceAccount)) {
                              blockedTypes = [
                                UserType.wmb,
                              ]
                            } else {
                              blockedTypes = [
                                UserType.kiosk,
                                UserType.mobile,
                                UserType.barrier,
                                UserType.wmb
                              ]
                            }

                            if (!isSysAdmin(session)) {
                              blockedTypes.push(UserType.service)
                            }
                          } else {
                            blockedTypes = isNewUser == true || (isNewUser == false && isServiceAccount) ? [
                              UserType.service,
                              UserType.wmb,
                            ] : [
                              UserType.service,
                              UserType.kiosk,
                              UserType.mobile,
                              UserType.barrier,
                              UserType.wmb
                            ];
                          }

                          for (const pt in UserType) {
                            if (isNaN(parseInt(pt))) {
                              if (blockedTypes.indexOf(parseInt(UserType[pt])) == -1) {
                                items.push({
                                  name: formatCamel(pt.toString()),
                                  value: UserType[pt],
                                });
                              }
                            }
                          }
                          return items;
                        }}
                      />

                      {isNewUser == false && (
                        <EditableField<User, string>
                          inputType={EditableFieldTypes.text}
                          label={t("input.labels.username")}
                          readOnly={true}
                          alwaysVisible={true}
                          onProvide={(data) => data.userName}
                          onChange={(data, value) => (data.userName = value)}
                        />
                      )}

                      {isServiceAccount == false && (
                        <EditableField<User, string>
                          inputType={EditableFieldTypes.text}
                          label={t("input.labels.email")}
                          onProvide={(data) => data.email}
                          onChange={(data, value) => (data.email = value)}
                        />
                      )}

                      {isServiceAccount == false && (<Fragment>
                        <EditableField<User, string>
                          inputType={EditableFieldTypes.text}
                          label={t("booking.labels.firstName")}
                          onProvide={(data) => data.firstName}
                          onChange={(data, value) => (data.firstName = value)}
                        />
                        {!isUserExternalRefReleased && isServiceAccount && isNewUser == true && (
                          <EditableField<User, string>
                            inputType={EditableFieldTypes.empty}
                            label={""}
                          />
                        )}
                        <EditableField<User, string>
                          inputType={EditableFieldTypes.text}
                          label={t("booking.labels.lastName")}
                          onProvide={(data) => data.lastName}
                          onChange={(data, value) => (data.lastName = value)}
                        />

                      </Fragment>)}
                      {isServiceAccount == true && (<Fragment>
                        <EditableField<User, string>
                          inputType={EditableFieldTypes.text}
                          label={t("input.labels.name")}
                          onProvide={(data) => data.firstName}
                          onChange={(data, value) => (data.firstName = value)}
                        />
                      </Fragment>)}
                      {isUserExternalRefReleased && isServiceAccount == false && (<Fragment>
                        <EditableField<User, string>
                          inputType={EditableFieldTypes.text}
                          maxLength={255}
                          label={t("input.labels.externalRef")}
                          onProvide={(data) => data.externalRef ?? ""}
                          onChange={(data, value) => (data.externalRef = value)}
                          testId={"user-external-ref-field"}
                        />
                      </Fragment>)}
                      {isCurrentUser == false && (
                        <Fragment>
                          <EditableField<User, string>
                            inputType={EditableFieldTypes.empty}
                            label={""}
                          />
                          <EditableField<User, boolean>
                            inputType={EditableFieldTypes.switch}
                            label={t("reports.enabled")}
                            onProvide={(data) => data.active}
                            onChange={(data, value) => (data.active = value)}
                          />
                        </Fragment>
                      )}

                      {isAgencyFlagReleased && (
                        <EditableField<User, boolean>
                          inputType={EditableFieldTypes.switch}
                          label={t("users.agency")}
                          onProvide={(data) => data.agency}
                          onChange={(data, value) => (data.agency = value)}
                        />
                      )}
                    </Grid>
                  </EditableGroup>
                </NotchedCard>

                <NotchedCard label={t("users.carpark")}>
                  <EditableGroup<IUserUpdate | IUserCreate>
                    id="carpark"
                    testId="user-carpark-group"
                    editType={EditableGroupType.alwaysEditing}
                    data={user}
                    dataFields={['defaultCarPark', 'carParkAccess']}
                    onDataChanged={async (data) => {
                      setUser({ ...user, ...data });
                      return true;
                    }}
                  >
                    <Grid container>
                      <Grid item xs={12} sm={6}>
                        <div style={{ marginTop: 20, paddingLeft: 10 }}>
                          <CarParkField
                            carParkAccess={(user as User).carParkAccess || ""}
                            label={t("users.homeCarpark")}
                            value={(user as User).defaultCarPark}
                            onChange={(value) => {
                              setUser({ ...user, defaultCarPark: value });
                            }}
                          ></CarParkField>
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <UserCarParkAccess
                          user={user as User}
                          onAccessListUpdate={(carParkAccess) => {
                            setUser({ ...user, carParkAccess: carParkAccess });
                          }}
                        ></UserCarParkAccess>
                      </Grid>
                    </Grid>
                  </EditableGroup>
                </NotchedCard>

                {isServiceAccount == false && (
                  <>
                    {isRbacReleased ? (
                      <>
                        <NotchedCard label={t("users.role")}>
                          <EditableGroup<IUserUpdate | IUserCreate>
                            id="roles"
                            testId="user-roles-group"
                            editType={EditableGroupType.alwaysEditing}
                            data={user}
                            dataFields={['roles']}
                            onDataChanged={async (data) => {
                              setUser({ ...user, ...data });
                              return true;
                            }}
                          >
                            <Grid container>
                              {props.availableRoles && props.availableRoles.length > 0 && props.availableRoles.map((role) => (
                                role.name !== "ParkIT.admin" && <EditableField<User, boolean>
                                  key={role.name}
                                  inputType={EditableFieldTypes.switch}
                                  label={mapRoleNameToDescription(role.name)} // TODO: use the role key to map to a translation key
                                  onProvide={() => isRoleAssigned(role.name)}
                                  onChange={(data, value) => {
                                    const currentRoles = (data as User).roles || [];
                                    let updatedRoles = [...currentRoles];

                                    if (value && !updatedRoles.includes(role.name)) {
                                      updatedRoles.push(role.name);
                                    } else if (!value && updatedRoles.includes(role.name)) {
                                      updatedRoles = updatedRoles.filter(r => r !== role.name);
                                    }

                                    (data as User).roles = updatedRoles;
                                  }}
                                  testId={`role-${role.name.toLowerCase().replace('_', '-')}-checkbox`}
                                  readOnly={isCurrentUser}
                                />
                              ))}
                            </Grid>
                          </EditableGroup>
                        </NotchedCard>

                        <NotchedCard label={t("users.permissions")}>
                          <EditableGroup<IUserUpdate | IUserCreate>
                            id="permissions"
                            testId="user-permissions-group"
                            editType={EditableGroupType.alwaysEditing}
                            data={user}
                            dataFields={['passwordNeverExpires', 'keyroomAccess', 'barrierAccess', 'pdaUser', 'showUserInLocationView']}
                            onDataChanged={async (data) => {
                              setUser({ ...user, ...data });
                              return true;
                            }}
                          >
                            <Grid container>
                              <EditableField<User, boolean>
                                inputType={EditableFieldTypes.switch}
                                label={t("users.passwordNeverExp")}
                                onProvide={(data) => data.passwordNeverExpires}
                                onChange={(data, value) => (data.passwordNeverExpires = value)}
                                testId="permission-password-never-expires-checkbox"
                              />
                              {isCurrentUser != true && (
                                <Fragment>
                                  <EditableField<User, boolean>
                                    inputType={EditableFieldTypes.switch}
                                    label={t("users.keyroomAccess")}
                                    onProvide={(data) => data.keyroomAccess}
                                    onChange={(data, value) =>
                                      (data.keyroomAccess = value)
                                    }
                                    testId="permission-keyroom-access-checkbox"
                                  />
                                  <EditableField<User, boolean>
                                    inputType={EditableFieldTypes.switch}
                                    label={t("users.barrierAccess")}
                                    onProvide={(data) => data.barrierAccess}
                                    onChange={(data, value) =>
                                      (data.barrierAccess = value)
                                    }
                                    testId="permission-barrier-access-checkbox"
                                  />
                                  <EditableField<User, boolean>
                                    inputType={EditableFieldTypes.switch}
                                    label={t("users.pdaUser")}
                                    onProvide={(data) => data.pdaUser}
                                    onChange={(data, value) => (data.pdaUser = value)}
                                    testId="permission-pda-user-checkbox"
                                  />
                                  {/* When this flag is depreacted, can remove this whole switch field */}
                                  {!isDeprecateShowUserInLocationViewReleased && <EditableField<User, boolean>
                                    inputType={EditableFieldTypes.switch}
                                    label={t("settings.visibleInLocationHierarchy")}
                                    onProvide={(data) => data.showUserInLocationView}
                                    onChange={(data, value) => (data.showUserInLocationView = value)}
                                    testId="permission-show-user-in-location-view-checkbox"
                                  />}
                                </Fragment>
                              )}
                            </Grid>
                          </EditableGroup>
                        </NotchedCard>
                      </>
                    ) : (
                      <NotchedCard label={t("users.role")}>
                        <EditableGroup<IUserUpdate | IUserCreate>
                          id="role"
                          testId="user-role-group"
                          editType={EditableGroupType.alwaysEditing}
                          data={user}
                          dataFields={['passwordNeverExpires', 'showUserInLocationView', 'pdaUser', 'busDriver', 'transportManager', 'mngDriver', 'keyroomAccess', 'barrierAccess', 'keyMaster']}
                          onDataChanged={async (data) => {
                            setUser({ ...user, ...data });
                            return true;
                          }}
                        >
                          <Grid container>
                            <EditableField<User, boolean>
                              inputType={EditableFieldTypes.switch}
                              label={t("users.passwordNeverExp")}
                              onProvide={(data) => data.passwordNeverExpires}
                              onChange={(data, value) => (data.passwordNeverExpires = value)}
                            />
                            <EditableField<User, boolean>
                              inputType={EditableFieldTypes.switch}
                              label={t("settings.driverVisibleInLM")}
                              onProvide={(data) => data.showUserInLocationView}
                              onChange={(data, value) => (data.showUserInLocationView = value)}
                            />
                            {isCurrentUser != true && (
                              <Fragment>
                                <EditableField<User, boolean>
                                  inputType={EditableFieldTypes.switch}
                                  label={t("users.pdaUser")}
                                  onProvide={(data) => data.pdaUser}
                                  onChange={(data, value) => (data.pdaUser = value)}
                                />
                                <EditableField<User, boolean>
                                  inputType={EditableFieldTypes.switch}
                                  label={t("users.busDriver")}
                                  onProvide={(data) => data.busDriver}
                                  onChange={(data, value) => (data.busDriver = value)}
                                />
                                <EditableField<User, boolean>
                                  inputType={EditableFieldTypes.switch}
                                  label={t("users.transportManager")}
                                  onProvide={(data) => data.transportManager}
                                  onChange={(data, value) => (data.transportManager = value)}
                                />
                                <EditableField<User, boolean>
                                  inputType={EditableFieldTypes.switch}
                                  label={t("users.mngDriver")}
                                  onProvide={(data) => data.mngDriver}
                                  onChange={(data, value) => (data.mngDriver = value)}
                                />
                                <EditableField<User, boolean>
                                  inputType={EditableFieldTypes.switch}
                                  label={t("users.keyroomAccess")}
                                  onProvide={(data) => data.keyroomAccess}
                                  onChange={(data, value) =>
                                    (data.keyroomAccess = value)
                                  }
                                />
                                <EditableField<User, boolean>
                                  inputType={EditableFieldTypes.switch}
                                  label={t("users.barrierAccess")}
                                  onProvide={(data) => data.barrierAccess}
                                  onChange={(data, value) =>
                                    (data.barrierAccess = value)
                                  }
                                />
                                {isKeyMasterRoleReleased && (
                                  <EditableField<User, boolean>
                                    inputType={EditableFieldTypes.switch}
                                    label={t("users.keyMaster")}
                                    onProvide={(data) => data.keyMaster}
                                    onChange={(data, value) => (data.keyMaster = value)}
                                  />
                                )}
                                <div style={{ height: 100 }}></div>
                              </Fragment>
                            )}
                          </Grid>
                        </EditableGroup>
                      </NotchedCard>
                    )}
                  </>
                )}

                {isCurrentUser == true && isServiceAccount == false && (
                  <NotchedCard label={t("users.changePassword")}>
                    <EditableGroup<IUserUpdate | IUserCreate>
                      id="pwd"
                      testId="user-password-group"
                      editType={EditableGroupType.alwaysEditing}
                      data={user}
                      onDataChanged={async () => {
                        // Password fields are handled by separate state, not user object
                        return true;
                      }}
                    >
                      <Grid container>
                        <EditableField<User, string>
                          useFullWidthWhenEditing={true}
                          inputType={EditableFieldTypes.password}
                          label={t("users.currentPassword")}
                          onProvide={() => currentPassword}
                          onChange={(data, value) => {
                            setCurrentPassword(value);
                          }}
                        />

                        <EditableField<User, string>
                          useFullWidthWhenEditing={true}
                          inputType={EditableFieldTypes.password}
                          label={t("users.newPassword")}
                          onProvide={() => newPassword}
                          onChange={(data, value) => {
                            setNewPassword(value);
                          }}
                          onValidate={(data, value) => {
                            if (value == confirmNewPassword) {
                              return null;
                            }
                            return t("users.passwordsDoNotMatch");
                          }}
                        />

                        <EditableField<User, string>
                          useFullWidthWhenEditing={true}
                          inputType={EditableFieldTypes.password}
                          label={t("users.confirmPassword")}
                          onProvide={() => confirmNewPassword}
                          onChange={(data, value) => {
                            setConfirmNewPassword(value);
                          }}
                        />
                      </Grid>
                    </EditableGroup>
                  </NotchedCard>
                )}
              </div>
            </ProgressView>
          </DialogContent>

          {isLoading == false && (
            <DialogActions>
              <Button
                onClick={async () => {
                  props.onClose(false, null);
                }}
                color="primary"
              >
                {t("general.cancel")}
              </Button>

              {(isCurrentUser == false && isNewUser == false && (isServiceAccount == false)) && (
                <Button
                  onClick={async () => {
                    setShowPasswordReset(true);
                  }}
                  color="primary"
                >
                  {t("users.resetPassword")}
                </Button>
              )}
              {((isAdmin(session) == true || isCurrentUser == true) && isNewUser == false && (isServiceAccount == false)) && (
                <Button
                  onClick={async () => {
                    setShowResetStaffPass(true);
                  }}
                  color="primary"
                >
                  {t("users.resetStaffPass")}
                </Button>
              )}
              {(isAdmin(session) == true && isCurrentUser == false && isNewUser == false && (isServiceAccount == true)) && (
                <Button
                  onClick={async () => {
                    setShowPasswordReset(true);
                  }}
                  color="primary"
                >
                  {t("users.resetServiceToken")}
                </Button>
              )}
              {isCreateServiceUserReleased && (isSysAdmin(session) == true && isCurrentUser == false && isNewUser == false && (isServiceAccount == true) && user.userType == UserType.service) && (
                <Button
                  onClick={async () => {
                    setShowTokenManagement(true);
                  }}
                  color="primary"
                >
                  {t("users.manageTokens")}
                </Button>
              )}
              {(
                isNewUser == false
                && (isServiceAccount == false)
                && ((user as User).location != null)
                && <Button
                  onClick={async () => {
                    labelPrinter.print(
                      generateUserPrintJob((user as User).location)
                    );
                  }}
                  color="primary"
                >
                  {t("users.printStaffPass")}
                </Button>
              )}
              <Button
                onClick={async () => {
                  // Validate role selection for new users and non-service accounts
                  if (isRbacReleased && isNewUser && !isServiceAccount) {
                    const roleValidationError = validateRoleSelection(user);
                    if (roleValidationError) {
                      alert(roleValidationError); // Using alert for now, replace with error dialog
                      return;
                    }
                  }

                  if (isNewUser == true) {
                    const newUserData = { ...user } as IUserCreate;

                    // Include RBAC roles when creating new user
                    if (isRbacReleased && (user as User).roles && (user as User).roles!.length > 0) {
                      newUserData.roles = (user as User).roles;
                    }

                    createUser.send(
                      session.current.context,
                      newUserData,
                      (result) => {
                        if (result == null) {
                          return;
                        }
                        setSaved(true);
                        setGeneratedUser(result);
                        setShowGeneratedUser(true);

                        if (isCreateServiceUserReleased && user.userType === UserType.service) {
                          setShowTokenManagement(true);
                        }
                      }
                    );
                    return;
                  } else {
                    const usr = { ...user } as IUserUpdate;

                    if (isCurrentUser == true) {
                      if (newPassword == confirmNewPassword) {
                        usr.currentPassword = currentPassword;
                        usr.newPassword = newPassword;
                      }
                    }

                    // Include RBAC roles when updating user
                    if (isRbacReleased) {
                      usr.roles = (user as User).roles || [];
                    }

                    updateUser.send(
                      session.current.context,
                      usr,
                      (updatedUser) => {
                        if (updatedUser == null) {
                          return;
                        }
                        setSaved(true);
                        props.onClose(true, updatedUser);
                      }
                    );
                  }
                }}
                color="primary"
              >
                {t("general.saveAndClose")}
              </Button>
            </DialogActions>
          )}
        </Dialog>
      )}

      <Dialog
        open={showDisableTwoFactor}
        keepMounted
        onClose={() => setShowDisableTwoFactor(false)}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">

            <Fragment>
              {t("users.confirmDisable2FA")}
            </Fragment>

          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDisableTwoFactor(false)} color="primary">
            {t("general.cancel")}
          </Button>
          <Button onClick={() => disableTwoFactor()} color="primary">
            {t("users.disable")}
          </Button>
        </DialogActions>
      </Dialog>


      <Dialog
        open={showDisableTwoFactorConfirmed}
        keepMounted
        onClose={() => setShowResetStaffPassConfirmed(false)}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            <Fragment>{t("users.2FADisabled")}</Fragment>
          </DialogContentText>
        </DialogContent>
        <DialogActions>

          <Button onClick={() => setShowDisableTwoFactorConfirmed(false)} color="primary">
            {t("general.close")}
          </Button>
        </DialogActions>
      </Dialog>



      <Dialog
        open={showPasswordReset}
        keepMounted
        onClose={() => setShowPasswordReset(false)}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            {(isNewUser == false && isServiceAccount && user.userType !== UserType.service) ? (
              <Fragment>{t("users.confirmResetST")}</Fragment>
            ) : (
              <Fragment>{t("users.confirmResetPassword")}</Fragment>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPasswordReset(false)} color="primary">
            {t("general.cancel")}
          </Button>
          <Button onClick={() => resetUsersPassword()} color="primary">
            {t("users.reset")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showResetStaffPass}
        keepMounted
        onClose={() => setShowResetStaffPass(false)}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            <Fragment>{t("users.prevStaffPassInval")}</Fragment>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowResetStaffPass(false)} color="primary">
            {t("general.cancel")}
          </Button>
          <Button onClick={() => resetStaffPass()} color="primary">
            {t("users.reset")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showResetStaffPassConfirmed}
        keepMounted
        onClose={() => setShowResetStaffPassConfirmed(false)}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            <Fragment>{t("users.staffPassReset")}</Fragment>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            labelPrinter.print(
              generateUserPrintJob((user as User).location)
            );
          }} color="primary">
            {t("input.labels.print")}
          </Button>
          <Button onClick={() => setShowResetStaffPassConfirmed(false)} color="primary">
            {t("general.close")}
          </Button>
        </DialogActions>
      </Dialog>

      {isCreateServiceUserReleased &&
        <Dialog
          open={showTokenManagement}
          aria-labelledby="alert-dialog-slide-title"
          aria-describedby="alert-dialog-slide-description"
        >
          {isTokenManagementV2Released ? (
            <ServiceUserTokenManagementV2
              key={`${session.current.site?.tenant.id}:${tokenManagementUser?.uuid}`}
              userUuid={tokenManagementUser?.uuid ?? ""}
              generatedUser={props.user ? null : generatedUser}
              onClose={closeTokenManagement}
            />
          ) : (
            <ServiceUserTokenManagement user={props.user} generatedUser={generatedUser} onClose={closeTokenManagement} />
          )}
        </Dialog>
      }

      <Dialog
        open={showGeneratedUser}
        keepMounted
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogContent>
          {generatedUser != null && (
            <>
              {generatedUser.user.userType == UserType.service
                ?
                <></>
                :
                <Fragment>
                  {generatedUser.user.credentialType == CredentialType.service &&
                    <DialogContentText id="alert-dialog-slide-description">
                      {t("users.configToken")}
                    </DialogContentText>
                  }
                  {generatedUser.user.credentialType != CredentialType.service &&
                    <DialogContentText id="alert-dialog-slide-description">
                      {t("users.changePasswordNext")}
                    </DialogContentText>
                  }
                  <DialogContentText id="alert-dialog-slide-description">
                    <table cellPadding={7} style={{ width: "100%" }}>
                      <tr>
                        <td style={{ textAlign: "right" }}>{t("input.labels.username")}:</td>
                        <td>
                          <b>{generatedUser.user.userName}</b>
                        </td>
                      </tr>

                      {generatedUser.user.email != null && (
                        <tr>
                          <td style={{ textAlign: "right", width: 150 }}>
                            {t("users.email")}:
                          </td>
                          <td>
                            <b>{generatedUser.user.email}</b>
                          </td>
                        </tr>
                      )}

                      {generatedUser.user.credentialType == CredentialType.service && (
                        <tr>
                          <td style={{ textAlign: "right", verticalAlign: "top" }}>{t("users.serviceToken")}:</td>
                          <td>
                            <div>
                              <textarea style={{ width: 300, height: 200 }}>{generatedUser.password}</textarea>
                            </div>
                          </td>
                        </tr>
                      )}

                      {generatedUser.user.credentialType != CredentialType.service && (
                        <tr>
                          <td style={{ textAlign: "right", verticalAlign: "top" }}>{t("users.newPassword")}:</td>
                          <td>
                            <div>
                              <b style={{ color: "blue" }}>{generatedUser.password}</b>
                            </div>
                          </td>
                        </tr>
                      )}

                    </table>
                  </DialogContentText>
                </Fragment>
              }
            </>
          )}
        </DialogContent>
        <DialogActions>
          {generatedUser != null && isServiceAccount == false && generatedUser.user.credentialType != CredentialType.service && (
            <Button
              onClick={async () => {
                if (generatedUser.user != null) {
                  labelPrinter.print(
                    generateUserPrintJob(generatedUser.user.location)
                  );
                }
              }}
              color="primary"
            >
              <b>{t("users.printStaffPass")}</b>
            </Button>
          )}

          <Button
            onClick={() => {
              setShowGeneratedUser(false);
              if (isNewUser == true) {
                if (generatedUser) {
                  if (generatedUser.user) {
                    props.onClose(true, generatedUser.user);
                    return;
                  }
                }
                props.onClose(true, null);
              }
            }}
            color="primary"
          >
            {t("general.ok")}
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default UserDetailDialog;
